import { InstructionStep, WorkflowStep } from '../enums/automation.enum';

/**
 * These two files define bidirectional lookup maps between step enum values and
 * their URL-safe string representations used in the `:step` route parameter.
 *
 * Each builder type has two maps:
 *
 * 1. `SLUG_MAP` (URL param → Enum): Used on component init to read the `:step`
 *    route parameter (e.g., `'security'`) and resolve it to the corresponding enum
 *    value, which is then matched to the stepper index to activate the correct step.
 *
 * 2. `STEP_TO_SLUG` (Enum → URL param): Used when the user clicks a step in the UI.
 *    The active step's enum value is converted to its slug and written into the URL
 *    via `navigateByUrl`, keeping the address bar in sync so the user can bookmark
 *    or share a direct link to the current step (e.g., `/edit-workflow/:id/security`).
 */

/**
 * Maps a URL slug to the corresponding WorkflowStep label.
 * Used to resolve the active step from the `:step` route parameter on component init.
 *
 * @example
 * WORKFLOW_STEP_SLUG_MAP['security'] // → WorkflowStep.SECURITY
 */
export const WORKFLOW_STEP_SLUG_MAP: Record<string, WorkflowStep> = {
  details: WorkflowStep.DETAILS,
  instructions: WorkflowStep.INSTRUCTIONS,
  security: WorkflowStep.SECURITY,
  admin: WorkflowStep.ADMIN,
  summary: WorkflowStep.SUMMARY,
};

/**
 * Maps a WorkflowStep label to its URL slug.
 * Used when the user navigates to a step in the UI to update the URL address bar.
 *
 * Note: The keys in the object are computed property names using the enum values to ensure type safety and maintainability.
 *
 * @example
 * WORKFLOW_STEP_TO_SLUG[WorkflowStep.SECURITY] // → 'security'
 */
export const WORKFLOW_STEP_TO_SLUG: Record<WorkflowStep, string> = {
  [WorkflowStep.DETAILS]: 'details',
  [WorkflowStep.INSTRUCTIONS]: 'instructions',
  [WorkflowStep.SECURITY]: 'security',
  [WorkflowStep.ADMIN]: 'admin',
  [WorkflowStep.SUMMARY]: 'summary',
};

/**
 * Maps a URL slug to the corresponding InstructionStep label.
 * Used to resolve the active step from the `:step` route parameter on component init.
 *
 * @example
 * INSTRUCTION_STEP_SLUG_MAP['ai-config'] // → InstructionStep.AI_CONFIG
 */
export const INSTRUCTION_STEP_SLUG_MAP: Record<string, InstructionStep> = {
  definition: InstructionStep.DEFINITION,
  'ai-config': InstructionStep.AI_CONFIG,
  types: InstructionStep.TYPES,
  security: InstructionStep.SECURITY,
  admin: InstructionStep.ADMIN,
  summary: InstructionStep.SUMMARY,
};

/**
 * Maps an InstructionStep label to its URL slug.
 * Used when the user navigates to a step in the UI to update the URL address bar.
 *
 * Note: The keys in the object are computed property names using the enum values to ensure type safety and maintainability.
 *
 * @example
 * INSTRUCTION_STEP_TO_SLUG[InstructionStep.AI_CONFIG] // → 'ai-config'
 */
export const INSTRUCTION_STEP_TO_SLUG: Record<InstructionStep, string> = {
  [InstructionStep.DEFINITION]: 'definition',
  [InstructionStep.AI_CONFIG]: 'ai-config',
  [InstructionStep.TYPES]: 'types',
  [InstructionStep.TEST]: 'test',
  [InstructionStep.SECURITY]: 'security',
  [InstructionStep.ADMIN]: 'admin',
  [InstructionStep.SUMMARY]: 'summary',
};

